import { useSyncExternalStore } from 'react'
import { esEscritorio } from './desktop'

// Estado de instalación de la PWA. Vive a NIVEL DE MÓDULO y se importa por efecto
// lateral desde main.tsx: 'beforeinstallprompt' se dispara muy temprano y UNA sola
// vez por carga, así que un listener dentro de un componente (BeeTopbar se monta por
// página) se perdería el evento.
//
// NO se llama e.preventDefault(): en Chromium eso SUPRIME el botón nativo de instalar
// de la barra de direcciones. Dejándolo pasar conviven los dos caminos, el nativo y
// nuestro ítem de menú (criterio de Guard).

type PromptOutcome = 'accepted' | 'dismissed'
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: PromptOutcome }>
}

let deferred: InstallPromptEvent | null = null
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

// iPadOS 13+ se reporta como Mac: hay que mirar los puntos táctiles.
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false
  const nav = navigator as Navigator & { maxTouchPoints?: number }
  return (
    /iphone|ipod|ipad/i.test(nav.userAgent) ||
    (nav.platform === 'MacIntel' && (nav.maxTouchPoints ?? 0) > 1)
  )
}

// ¿Corre como app instalada? Tres señales porque ninguna cubre todo: en iOS el
// A2HS no reporta display-mode de forma fiable y la buena es navigator.standalone
// (no estándar, y por eso CSS no puede leerla — ver useStandalone), y en la app de
// escritorio NINGUNA de las dos se cumple: Electron carga la web en una ventana
// normal, así que matchMedia dice 'browser'. Sin esta tercera señal la app de
// escritorio se quedaba sin barra de pestañas y ofreciendo "Instalar app" DENTRO
// de la app instalada.
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  if (esEscritorio()) return true
  const nav = navigator as Navigator & { standalone?: boolean }
  return window.matchMedia?.('(display-mode: standalone)').matches === true || nav.standalone === true
}

const mql = typeof window !== 'undefined' ? window.matchMedia?.('(display-mode: standalone)') : undefined

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    deferred = e as InstallPromptEvent
    emit()
  })
  window.addEventListener('appinstalled', () => {
    deferred = null
    emit()
  })
  // CLAVE y ausente en Guard: el display-mode cambia SIN recargar (en escritorio se
  // instala desde la barra de direcciones y la ventana pasa a standalone en caliente).
  // Sin esto la barra de tabs se quedaría congelada hasta el siguiente re-render.
  mql?.addEventListener?.('change', emit)
}

export function promptInstall(): Promise<PromptOutcome | null> {
  const evt = deferred
  if (!evt) return Promise.resolve(null)
  // A null ANTES del await: el evento es de un solo uso y un segundo clic
  // reintentaría con uno ya consumido.
  deferred = null
  emit()
  return evt
    .prompt()
    .then(() => evt.userChoice)
    .then((c) => c.outcome)
    .catch(() => null)
}

// Snapshot memoizado: useSyncExternalStore compara por identidad y un objeto nuevo
// en cada llamada provocaría un bucle de renders.
let snapshot = { canPrompt: false, standalone: false }
function getSnapshot() {
  // En escritorio NUNCA se puede ofrecer instalar: aunque Chromium disparara
  // beforeinstallprompt, instalar la PWA desde dentro del .app no significa nada.
  const canPrompt = deferred !== null && !esEscritorio()
  const standalone = isStandalone()
  if (canPrompt !== snapshot.canPrompt || standalone !== snapshot.standalone) {
    snapshot = { canPrompt, standalone }
  }
  return snapshot
}
function subscribe(cb: () => void) {
  listeners.add(cb)
  return () => listeners.delete(cb)
}
const SERVER_SNAPSHOT = { canPrompt: false, standalone: false }

export function useInstall() {
  const s = useSyncExternalStore(subscribe, getSnapshot, () => SERVER_SNAPSHOT)
  return { ...s, ios: isIOS() }
}

// Azúcar para quien solo necesita saber si va instalada (la barra de tabs).
export function useStandalone(): boolean {
  return useInstall().standalone
}
